import { create } from "zustand"

import { lerPreferencia, salvarPreferencia } from "@/lib/preferencias"

/**
 * Estado de sessão (UI apenas). O access token vive SOMENTE em memória
 * (lib/auth); o refresh token fica no cookie HttpOnly do backend.
 * Nenhuma persistência em localStorage/sessionStorage.
 */
export type PermissaoModo = "HERDAR" | "DEFINIR"

export interface GrupoInfo {
  id: string
  nome: string
  /** HERDAR = token/JWT; DEFINIR = permissões cadastradas por usuário no grupo. */
  permissaoModo?: PermissaoModo
  /** Permissões efetivas do usuário logado naquele grupo (retornadas pelo /me). */
  permissoes?: string[]
}

export type TipoSessao = "INTERNO" | "CIDADAO"

interface SessionState {
  authenticated: boolean
  /** Origem da sessão: usuário interno (realm testes) ou cidadão (gov.br). */
  tipo: TipoSessao | null
  username: string | null
  /** Nome completo (/me), usado na UI em vez do código do usuário. */
  fullName: string | null
  /** E-mail institucional (/me). */
  email: string | null
  /** Perfis (realm roles SIGI_*). */
  roles: string[]
  /** Permissões efetivas do /me (derivadas dos perfis + subroles sigi:* do token). */
  permissions: string[]
  groups: GrupoInfo[]
  /** Grupo ativo (header X-Grupo). null = sem grupo ativo (admin em "Todos os grupos"). */
  grupoAtivo: GrupoInfo | null
  setAuthenticated: (authenticated: boolean) => void
  setTipo: (tipo: TipoSessao | null) => void
  setProfile: (username: string | null, roles: string[], permissions?: string[]) => void
  setIdentidade: (fullName: string | null, email: string | null) => void
  setGroups: (groups: GrupoInfo[]) => void
  setGrupoAtivo: (grupo: GrupoInfo | null) => void
  clear: () => void
}

export const useSessionStore = create<SessionState>((set) => ({
  authenticated: false,
  tipo: null,
  username: null,
  fullName: null,
  email: null,
  roles: [],
  permissions: [],
  groups: [],
  grupoAtivo: null,
  setAuthenticated: (authenticated) => set({ authenticated }),
  setTipo: (tipo) => set({ tipo }),
  setProfile: (username, roles, permissions = []) => set({ username, roles, permissions }),
  setIdentidade: (fullName, email) => set({ fullName, email }),
  setGroups: (groups) => set({ groups }),
  setGrupoAtivo: (grupo) => set({ grupoAtivo: grupo }),
  clear: () =>
    set({
      authenticated: false,
      tipo: null,
      username: null,
      fullName: null,
      email: null,
      roles: [],
      permissions: [],
      groups: [],
      grupoAtivo: null,
    }),
}))

type Theme = "light" | "dark" | "system"

interface ThemeState {
  theme: Theme
  resolvedTheme: "light" | "dark"
  setTheme: (theme: Theme) => void
}

const inicial = window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light"

/** Tema persistido (só existe com consentimento de preferências). */
function temaInicial(): Theme {
  const salvo = lerPreferencia("tema")
  return salvo === "light" || salvo === "dark" || salvo === "system" ? salvo : "system"
}

const tema = temaInicial()

export const useThemeStore = create<ThemeState>((set) => ({
  theme: tema,
  resolvedTheme: tema === "system" ? inicial : tema,
  setTheme: (theme) => {
    salvarPreferencia("tema", theme)
    set({
      theme,
      resolvedTheme: theme === "system" ? inicial : theme,
    })
  },
}))

/**
 * Aplica o tema padrão do sistema somente quando o usuário ainda não escolheu
 * uma preferência (cookie/localStorage). Não persiste nada — a escolha
 * explícita do usuário continua tendo precedência.
 */
export function aplicarTemaPadrao(temaPadrao: string) {
  if (lerPreferencia("tema")) return
  if (temaPadrao !== "light" && temaPadrao !== "dark" && temaPadrao !== "system") return
  useThemeStore.setState({
    theme: temaPadrao,
    resolvedTheme: temaPadrao === "system" ? inicial : temaPadrao,
  })
}

export function applyTheme(resolved: "light" | "dark") {
  document.documentElement.classList.toggle("dark", resolved === "dark")
}
